import React, {useEffect, useRef, useState} from 'react';
import {PageInterface} from '@taopix/taopix-core/dist/types/types';
import {
  Button,
  PopOut,
  NumberControl,
  Link,
  Horizontal,
  Vertical,
  HelpTip,
  CheckCircleIcon,
  ThemeName,
} from '@taopix/taopix-design-system';
import {useTranslation} from 'react-i18next';
import {getScrollParent} from '../../Utils/Common';
import {useTaopix} from '../../Components/Context/TaopixContext';

type RetroQuantityButtonPropTypes = {
  page: PageInterface;
  onChangeQty: (truthy: boolean, page: PageInterface) => void;
};

const RetroQuantityButton = ({page, onChangeQty}: RetroQuantityButtonPropTypes) => {
  const app = useTaopix();
  const [quantityPopoutOpen, setQuantityPopoutOpen] = useState<boolean>(false);
  const [controlValue, setControlValue] = useState<number>(page.qty === 0 ? 1 : page.qty);
  const {t} = useTranslation();
  const [tipOpen, setTipOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const updatePrintQuantity = (increment: boolean) => {
    if (controlValue === 1 && !increment) return;
    const changeValue = increment ? 1 : -1;
    setControlValue(controlValue + changeValue);
    onChangeQty(increment, page);
  };

  const applyToAll = () => {
    const newQuantity = page.qty;
    app.core
      .get('stateManager')
      .call('bulkUpdateQty', {qty: newQuantity})
      .then(() => {
        setTipOpen(true);
      })
      .catch(error => {
        console.error(error);
      });
  };

  useEffect(() => {
    const scrollElement = getScrollParent(buttonRef?.current);
    // Add a scroll listener to the scrollParent to close the popout when it is scrolled.
    if (quantityPopoutOpen) {
      scrollElement?.addEventListener('scroll', closePopout);
    } else {
      setQuantityPopoutOpen(false);
    }
    return () => {
      // Remove the handler
      scrollElement?.removeEventListener('scroll', closePopout);
    };
  }, [quantityPopoutOpen]);

  /**
   * Closes the popup.
   */
  const closePopout = () => {
    if (quantityPopoutOpen) {
      // Remove the scroll handler from the scrollParent
      const scrollElement = getScrollParent(buttonRef?.current);
      scrollElement?.removeEventListener('scroll', closePopout);
      // Close the popout
      setQuantityPopoutOpen(false);
    }
  };

  return (
    <>
      <Button
        id={`printQuantityButton-${page.pagenumber}`}
        label={`x ${page.qty === 0 ? 1 : page.qty}`}
        ref={buttonRef}
        buttonStyle={'tertiary'}
        size={'small'}
        arrow={'down'}
        onClick={() => {
          setQuantityPopoutOpen(!quantityPopoutOpen);
          setControlValue(page.qty === 0 ? 1 : page.qty);
        }}
      />
      <PopOut
        open={quantityPopoutOpen}
        anchorId={`printQuantityButton-${page.pagenumber}`}
        onClose={() => setQuantityPopoutOpen(false)}
        className={'p-xs flex-col space-y-sm items-start'}
        theme={'tpx-theme-workarea'}
        themeTip={'Workarea'}
        contentTheme={ThemeName.Popouts}
        excludeIdFromClickOutside={`printQuantityButton-${page.pagenumber}`}
        edgeBehaviour={'flip'}
      >
        <p>{t('*:str_LabelPrintQuantity')}</p>
        <NumberControl
          value={controlValue}
          onButtonClick={increment => updatePrintQuantity(increment)}
        />
        <Link onClick={() => applyToAll()} className={'h-xl'}>
          {t('*:str_LabelApplyToAll')}
        </Link>
      </PopOut>

      <HelpTip
        open={tipOpen}
        message={t('*:str_MessageAppliedQuantityToAllPrints').replace('^0', String(controlValue))}
        anchorId={'viewerToolbar'}
        anchorOrigin={{vertical: Vertical.Bottom, horizontal: Horizontal.Left}}
        transformOrigin={{vertical: Vertical.Top, horizontal: Horizontal.Left}}
        offset={{x: 10, y: 0}}
        className={'max-w-[400rem]'}
        buttonFunction={() => setTipOpen(false)}
        onClose={() => setTipOpen(false)}
        excludeIdFromClickOutside={'workarea'}
        buttonLabel={t('*:str_ButtonOK')}
        icon={<CheckCircleIcon />}
        repositionOnResize
        hideAfter={5000}
      />
    </>
  );
};

export default RetroQuantityButton;
